import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Tooltip } from 'antd';
import { incidentService } from '../services/incidentService';
import { StatusMetricGroup } from './StatusMetricGroup';
import { StatusMetricCard } from './StatusMetricCard';

/**
 * 🚨 Current Incidents Container
 * 
 * Displays active and exercise incidents as colored pill badges.
 * Shows empty state when no incidents exist.
 * Clicking pills navigates to filtered incidents page.
 * 
 * @author Crisis Compass Development Team
 * @version 1.0.0 - TopCon Dashboard Implementation
 */
const CurrentIncidentsContainer = ({ orgId, cmtId, includeChildren = true }) => {
  const navigate = useNavigate();
  const [incidentsData, setIncidentsData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!orgId) {
      setLoading(false);
      return;
    }

    console.log('🚨 CurrentIncidentsContainer: Setting up subscription for org:', orgId, 'CMT:', cmtId);

    const setupSubscription = async () => {
      try {
        const unsubscribe = await incidentService.subscribeCurrentIncidents(
          orgId,
          cmtId,
          { includeChildren },
          (data) => {
            console.log('🚨 CurrentIncidentsContainer: Received data:', data);
            setIncidentsData(data);
            setLoading(false);
            setError(null);
          },
          (error) => {
            console.error('🚨 CurrentIncidentsContainer: Error:', error);
            setError(error.message);
            setLoading(false);
          }
        );

        return unsubscribe;
      } catch (error) {
        console.error('🚨 CurrentIncidentsContainer: Setup error:', error);
        setError(error.message);
        setLoading(false);
        return () => {}; // Return no-op cleanup function
      }
    };

    let unsubscribe;
    setupSubscription().then(unsub => {
      unsubscribe = unsub;
    });

    return () => {
      console.log('🚨 CurrentIncidentsContainer: Cleaning up subscription');
      if (unsubscribe) {
        unsubscribe();
      }
    };
  }, [orgId, cmtId, includeChildren]);

  // Handle pill click - navigate to incidents page with filter
  const handlePillClick = (category, incidentType) => {
    console.log('🚨 CurrentIncidentsContainer: Pill clicked:', category, incidentType);
    
    // Filter by the actual incident type
    const filterState = { crisisType: incidentType };

    navigate('/incidents', { 
      state: { 
        filters: filterState,
        fromDashboard: true 
      } 
    });
  };


  const total = incidentsData?.total || 0;
  const counts = incidentsData?.byCategory || {};

  if (loading) {
    return (
      <StatusMetricGroup
        title="Current Incidents"
        testId="current-incidents-group"
        totalCount="..."
      >
        <div style={{ 
          display: 'flex', 
          justifyContent: 'center', 
          alignItems: 'center',
          height: '100%', // Fill available height
          color: '#8c8c8c',
          fontSize: '14px'
        }}>
          Loading...
        </div>
      </StatusMetricGroup>
    );
  }

  if (error) {
    return (
      <StatusMetricGroup
        title="Current Incidents"
        testId="current-incidents-group"
        totalCount="Error"
      >
        <div style={{ 
          display: 'flex', 
          justifyContent: 'center', 
          alignItems: 'center',
          height: '100%', // Fill available height
          color: '#cf1322',
          fontSize: '12px',
          textAlign: 'center'
        }}>
          Error loading incidents
        </div>
      </StatusMetricGroup>
    );
  }

  // Create data cards based on actual incident types from the data
  const activeIncidentCards = [];
  
  // Get color for each incident type
  const getColorForType = (type) => {
    const colors = {
      'Medical Emergency': { active: "bg-orange-500 text-white", inactive: "bg-neutral-100 text-neutral-600" },
      'Suspicious Item / Bomb Threat': { active: "bg-amber-500 text-white", inactive: "bg-neutral-100 text-neutral-600" },
      'Active Threat': { active: "bg-red-600 text-white", inactive: "bg-neutral-100 text-neutral-600" },
      'Fire': { active: "bg-red-500 text-white", inactive: "bg-neutral-100 text-neutral-600" },
      'Flood': { active: "bg-blue-500 text-white", inactive: "bg-neutral-100 text-neutral-600" },
      'Cyber Incident': { active: "bg-purple-500 text-white", inactive: "bg-neutral-100 text-neutral-600" },
      'Exercise': { active: "bg-green-500 text-white", inactive: "bg-neutral-100 text-neutral-600" }
    };
    return colors[type] || { active: "bg-gray-600 text-white", inactive: "bg-neutral-100 text-neutral-600" };
  };
  
  // Create cards ONLY for incident types that have count > 0
  // BUT use GREY styling like Staff/Site Status cards
  if (incidentsData.byType) {
    Object.entries(incidentsData.byType).forEach(([incidentType, count]) => {
      if (count > 0) {
        activeIncidentCards.push({
          category: incidentType.toLowerCase().replace(/\s+/g, '_'),
          count: count,
          label: incidentType,
          activeColorClass: 'bg-neutral-100 text-neutral-600', // GREY like Staff/Site Status
          inactiveColorClass: 'bg-neutral-100 text-neutral-600' // GREY like Staff/Site Status
        });
      }
    });
  }

  return (
    <StatusMetricGroup
      title="Current Incidents"
      testId="current-incidents-group"
      totalCount={total}
    >
      {activeIncidentCards.length === 0 ? (
        // Empty state when no active incidents
        <div style={{ 
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          height: '100%',
          color: '#8c8c8c',
          fontSize: '12px',
          textAlign: 'center'
        }}>
          No active incidents
        </div>
      ) : (
        // Show data cards ONLY for active incident types (count > 0) with GREY styling
        activeIncidentCards.map((card) => (
          <StatusMetricCard
            key={card.category}
            label={card.label}
            count={card.count}
            activeColorClass={card.activeColorClass}
            inactiveColorClass={card.inactiveColorClass}
            testId={`incidents-${card.category}`}
            onClick={() => handlePillClick(card.category, card.label)}
          />
        ))
      )}
    </StatusMetricGroup>
  );
};

export default CurrentIncidentsContainer;
